import { isOfType } from 'common/helpers'
import type { WellId } from 'common/types'

import type {
  ContentId,
  CrmsAttachmentId,
  CrmsFieldId,
  CrmsLicenseAreaId,
  CrmsProjectResultId,
  KsuCompanyId,
  KsuFieldId,
  KsuLicenseAreaId,
  KsuWellId,
} from './types'
import type { UploadedFileStatusType } from './uploads'

const ImportDBResultViewModeArray = ['geodb', 'crms', 'geomate', 'alldb'] as const
export type ImportDBResultViewMode = (typeof ImportDBResultViewModeArray)[number]
export const isCanImportFrom = isOfType(ImportDBResultViewModeArray)

const ImportFilesResultViewModeArray = [
  'wells',
  'gis',
  'markers',
  'survey3D',
  'survey2D',
  'shots',
  'rigis',
  'inclinometry',
  'horizons',
  'polygons',
  'maps',
  'faults',
  'points',
  'bulk',
] as const

export type ImportFilesResultViewMode = (typeof ImportFilesResultViewModeArray)[number]

export type GeoDBSearchQuery = {
  company?: string
  field?: string
  licenseArea?: string
}

export type GeomateSearchQuery = {
  user: string
  entityType: string
}

export type CrmsSearchQuery = {
  identifier?: string
  territory?: string
  startYear?: number
  endYear?: number
  executer?: string
}

export type OverallSearchQuery = {
  mode: ImportDBResultViewMode
  globalSearch?: string
  geodb?: GeoDBSearchQuery
  crms?: CrmsSearchQuery
  geomate?: GeomateSearchQuery
}

export type PageDestination = 'next' | 'prev' | number

/** Данные об отображаемой странице. */
type PageInfo = {
  page: number
  firstRow: number
  rows: number
}

/** Структура для организации пагинации некоего датасета. */
export type PaginationInfo = {
  page: PageInfo
  prevPage: PageInfo
  pageCount: number
  rowCount: number
}

// results types
// crms

export type CrmsAttachment = {
  id: CrmsAttachmentId
  contentBucket: string
  contentFileName: string
  contentSize?: number | null
  contentId: ContentId
  path: string
  coordinateSystem?: string
  files?: CrmsAttachment[]
  type: 'folder' | 'file'
  state: UploadedFileStatusType
}

export type CrmsProjectResult = {
  id: CrmsProjectResultId
  identifier: string
  territory: string
  startYear?: number | null
  endYear?: number | null
  executer: string
  projectName: string
  fields?: CrmsField[] | null
  licenceAreas?: CrmsLicenseArea[] | null
  creationDate: string
}

type CrmsField = {
  id: CrmsFieldId
  name: string
}

type CrmsLicenseArea = {
  id: CrmsLicenseAreaId
  name: string
}

// geodb

export type KsuWellsSearchGroup = {
  by: 'Company' | 'Field' | 'LicenseArea'
  id: KsuFieldId
  name: string
  wellsCount: number
}

export type KsuWell = {
  ksuUid: KsuWellId
  wellUid: WellId
  wellName: string
  changedAt: string
  state: UploadedFileStatusType
}

export interface KsuCompany {
  ksuUid: KsuCompanyId
  name: string
  fields?: KsuField[]
  licenseAreas?: KsuLicenseArea[]
}

export type KsuField = {
  ksuUid: KsuFieldId
  name: string
}

export type KsuLicenseArea = {
  ksuUid: KsuLicenseAreaId
  name: string
}

/**
 * Свойства категории любого вида.
 */
type OverallSearchResultCategoryCommon = {
  /** Уникальный идентификатор */
  id: string

  /** Название для пользователя */
  name: string

  /**
   * Количество подэлементов в разделе.
   * Для ГеоБД - количество скважин.
   */
  count?: number
}

/** Категория результата поиска по ГеоБД */
type GeoDBSearchResultCategory = OverallSearchResultCategoryCommon & {
  source: 'geodb'
  data: KsuWellsSearchGroup
}

/** Категория результата поиска по Црмс */
type CrmsSearchResultCategory = OverallSearchResultCategoryCommon & {
  source: 'crms'
  data: CrmsProjectResult
}

/** Категория в списке категорий результатов поиска - корень одного из деревьев результатов. */
export type OverallSearchResultCategory = GeoDBSearchResultCategory | CrmsSearchResultCategory

/** Результата поиска по категории из ГеоБД. */
type SearchInGeoDBCategoryResult = {
  source: 'geodb'
  wells: KsuWell[]
}

/** Результат поиска по категории из Црмс. */
type SearchInCrmsCategoryResult = {
  source: 'crms'
  attachments: CrmsAttachment[]
}

/** Категория в списке категорий результатов поиска - корень одного из деревьев результатов. */
export type SearchInCategoryResult = SearchInGeoDBCategoryResult | SearchInCrmsCategoryResult

/** Выделенный узел в таблице (дереве) результатов. */
type OverallSearchActiveNode = {
  mode: ImportFilesResultViewMode
  id: string
  name: string
}

type OverallSearchStatus = 'wait' | 'loading' | 'done' | 'failed'

export type OverallSearchState = {
  status: OverallSearchStatus

  /** Параметры поиска. */
  query: OverallSearchQuery

  /** Справочник ДО КСУ НСИ для фильтрации запросов из геоБД. */
  ksuCompanies: KsuCompany[]

  /** Результаты глобального поиска - только категории. */
  result: OverallSearchResultCategory[]

  /** Выделенный результат в списке категорий результатов поиска. */
  activeCategoryId?: string

  /** Пагинация результатов поиска по категории. Если нет, выводится все сразу. */
  categoryPagination?: PaginationInfo

  /** Результаты поиска в выделенной категории. */
  categoryResult?: SearchInCategoryResult

  /** Выделенный узел в таблице (дереве) результатов. */
  activeNode?: OverallSearchActiveNode
}
